import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * App-level error boundary.
 *
 * Catches any uncaught render exception in the tree below it and shows a
 * BR-PT recovery screen instead of unmounting into a blank SPA. Errors are
 * forwarded to Sentry only when the frontend DSN is configured (same
 * DSN-gated, lazily-loaded pattern as src/index.tsx) so the boundary keeps
 * no hard dependency on Sentry.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (process.env.REACT_APP_SENTRY_DSN_FRONTEND) {
      import("@sentry/react")
        .then((Sentry) => {
          Sentry.captureException(error, {
            extra: { componentStack: info.componentStack },
          });
        })
        .catch(() => {
          /* Sentry unavailable — non-critical */
        });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "20px",
          padding: "24px",
          textAlign: "center",
          background: "#0a0a1a",
          color: "#ffffff",
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <h1 style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}>
          Algo deu errado
        </h1>
        <p
          style={{
            fontSize: "15px",
            lineHeight: 1.5,
            margin: 0,
            maxWidth: "320px",
            color: "#c7c7d1",
          }}
        >
          Encontramos um problema inesperado. Recarregue a página para
          continuar.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            marginTop: "4px",
            padding: "12px 24px",
            borderRadius: "12px",
            border: "none",
            background: "#FFD900",
            color: "#0a0a1a",
            fontSize: "15px",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Recarregar página
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
